/**
 * Cross-tab communication for web browser enhancement
 * Signals the original tab to close when magic link is verified in a new tab
 * 
 * Mobile apps ignore this - it's a web-only UX enhancement
 */

const CLOSE_ORIGINAL_TAB_KEY = 'mental-mint-close-tab';

// How long to keep the signal in localStorage before cleanup
const SIGNAL_CLEANUP_DELAY_MS = 1000;

/**
 * Creates a generic cross-tab signal/listener pair using localStorage events
 * Returns [signal, listen] functions
 */
function createCrossTabSignal(key: string): [() => void, (callback: () => void) => () => void] {
  const signal = () => {
    try {
      localStorage.setItem(key, Date.now().toString());
      setTimeout(() => localStorage.removeItem(key), SIGNAL_CLEANUP_DELAY_MS);
    } catch (error) {
      // Silently fail - this is just a UX enhancement
    }
  };
  
  const listen = (callback: () => void) => {
    const handler = (e: StorageEvent) => {
      if (e.key === key && e.newValue) callback();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  };
  
  return [signal, listen];
}

// Create the close original tab signal/listener pair
const [signalCloseOriginalTab, listenForCloseOriginalTab] = createCrossTabSignal(CLOSE_ORIGINAL_TAB_KEY);

export { signalCloseOriginalTab, listenForCloseOriginalTab };
